import { useState } from "react";

const ExpandIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" />
    <line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" />
  </svg>
);
const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const ImageIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" />
    <polyline points="21 15 16 10 5 21" />
  </svg>
);

const TYPE_LABELS = {
  portal: "Portal screenshot",
  howto:  "Step-by-step guide",
  form:   "Form preview",
  icon:   "Service illustration",
};

const TYPE_COLORS = {
  portal: { bg: "#eff6ff", border: "#bfdbfe", badge: "#dbeafe", badgeText: "#1e40af" },
  howto:  { bg: "#fefce8", border: "#fde68a", badge: "#fef3c7", badgeText: "#92400e" },
  form:   { bg: "#f0fdf4", border: "#bbf7d0", badge: "#dcfce7", badgeText: "#15803d" },
  icon:   { bg: "#faf5ff", border: "#e9d5ff", badge: "#ede9fe", badgeText: "#6b21a8" },
};

// ── Lightbox ─────────────────────────────────────────────
function Lightbox({ image, onClose }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(0,0,0,0.85)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "20px",
      }}
    >
      <div onClick={e => e.stopPropagation()} style={{ maxWidth: "90vw", maxHeight: "90vh", position: "relative" }}>
        <button
          onClick={onClose}
          style={{
            position: "absolute", top: "-36px", right: 0,
            background: "rgba(255,255,255,0.15)", border: "none",
            color: "#fff", borderRadius: "6px", padding: "4px 8px",
            cursor: "pointer", display: "flex", alignItems: "center", gap: "4px",
            fontSize: "12px",
          }}
        >
          <CloseIcon /> Close
        </button>
        <img
          src={image.url}
          alt={image.title}
          style={{ maxWidth: "90vw", maxHeight: "80vh", borderRadius: "8px", display: "block" }}
          onError={e => { e.target.style.display = "none"; }}
        />
        {image.caption && (
          <p style={{ color: "#e5e7eb", fontSize: "12px", marginTop: "10px", textAlign: "center", lineHeight: 1.5 }}>
            {image.caption}
          </p>
        )}
      </div>
    </div>
  );
}

// ── Single image card ─────────────────────────────────────
function SingleImageCard({ image }) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [loaded, setLoaded]   = useState(false);
  const [error, setError]     = useState(false);

  const colors = TYPE_COLORS[image.type] || TYPE_COLORS.portal;
  const typeLabel = TYPE_LABELS[image.type] || "Image";

  return (
    <>
      <div style={{
        background: colors.bg,
        border: `0.5px solid ${colors.border}`,
        borderRadius: "var(--border-radius-lg)",
        overflow: "hidden",
        maxWidth: "440px",
        marginTop: "6px",
        fontFamily: "var(--font-sans)",
      }}>
        {/* Type badge */}
        <div style={{ padding: "8px 12px 0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{
            fontSize: "10px", fontWeight: "500", letterSpacing: "0.06em",
            textTransform: "uppercase", color: colors.badgeText,
            background: colors.badge, padding: "2px 8px", borderRadius: "4px",
          }}>
            {typeLabel}
          </span>
          <button
            onClick={() => !error && setLightboxOpen(true)}
            title="View full size"
            style={{
              background: "none", border: "none", cursor: error ? "default" : "pointer",
              color: colors.badgeText, display: "flex", alignItems: "center",
              gap: "4px", fontSize: "11px", padding: "2px 4px",
              opacity: error ? 0.3 : 1,
            }}
          >
            <ExpandIcon /> Enlarge
          </button>
        </div>

        {/* Title */}
        <p style={{ margin: "4px 12px 8px", fontSize: "13px", fontWeight: "500", color: "var(--color-text-primary)" }}>
          {image.title}
        </p>

        {/* Image area */}
        <div
          onClick={() => !error && setLightboxOpen(true)}
          style={{
            cursor: error ? "default" : "pointer",
            background: "var(--color-background-secondary)",
            minHeight: "140px",
            display: "flex", alignItems: "center", justifyContent: "center",
            position: "relative", overflow: "hidden",
          }}
        >
          {/* Placeholder — shown while loading or on error */}
          {(!loaded || error) && (
            <div style={{
              position: "absolute", inset: 0,
              display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center",
              gap: "8px",
            }}>
              <div style={{ color: "var(--color-text-tertiary)" }}><ImageIcon /></div>
              <p style={{ fontSize: "12px", color: "var(--color-text-tertiary)", textAlign: "center", padding: "0 16px" }}>
                {error ? "Image coming soon" : "Loading..."}
              </p>
              {error && (
                <span style={{
                  fontSize: "10px", fontWeight: "500", letterSpacing: "0.05em",
                  textTransform: "uppercase", color: colors.badgeText,
                  background: colors.badge, padding: "2px 8px", borderRadius: "4px",
                }}>
                  Coming soon
                </span>
              )}
            </div>
          )}

          {/* Actual image */}
          <img
            src={image.url}
            alt={image.title}
            onLoad={() => setLoaded(true)}
            onError={() => setError(true)}
            style={{
              width: "100%", display: loaded && !error ? "block" : "none",
              maxHeight: "220px", objectFit: "cover",
            }}
          />
        </div>

        {/* Caption */}
        {image.caption && (
          <p style={{
            margin: "0", padding: "8px 12px 10px",
            fontSize: "11px", color: "var(--color-text-secondary)", lineHeight: 1.5,
          }}>
            {image.caption}
          </p>
        )}
      </div>

      {lightboxOpen && <Lightbox image={image} onClose={() => setLightboxOpen(false)} />}
    </>
  );
}

// ── Main export — renders one or more image cards ─────────
export default function ImageCard({ images }) {
  if (!images || images.length === 0) return null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "4px" }}>
      {images.map(img => (
        <SingleImageCard key={img.id} image={img} />
      ))}
    </div>
  );
}